/**
 * Vertical navigation menu component.
 * Modern glassmorphism sidebar with gradient active states and section grouping.
 */

// React Imports
import { useNavigate, useLocation } from 'react-router-dom';

// MUI Imports
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// Menu Items
import { menuSections, type MenuSection } from '../menuItems';

// Component Imports
import Logo from '@layouts/components/shared/Logo';

// Store
import { useContextStore } from '../../store/contextStore';

// Utils
import { ROUTES } from '@/utils/routes';

/** Explicit sidebar color tokens — no nested CSS var() chains */
const SIDEBAR_COLORS = {
  dark: {
    bg: 'linear-gradient(180deg, #111118 0%, #0B0B10 100%)',
    text: '#C8CDD5',            // ~80 % white — always readable on dark bg
    textActive: '#FFFFFF',
    textMuted: '#6B7280',       // section headers, version
    icon: '#9CA3AF',
    iconActive: '#FFFFFF',
    hoverBg: 'rgba(244, 63, 94, 0.10)',
    sectionBorder: 'rgba(255, 255, 255, 0.06)',
  },
  light: {
    bg: 'linear-gradient(180deg, #FAFBFC 0%, #F1F5F9 100%)',
    text: '#334155',
    textActive: '#FFFFFF',
    textMuted: '#94A3B8',
    icon: '#64748B',
    iconActive: '#FFFFFF',
    hoverBg: 'rgba(244, 63, 94, 0.06)',
    sectionBorder: 'rgba(15, 23, 42, 0.06)',
  },
};

interface VerticalNavProps {
  open?: boolean;
  onClose?: () => void;
  width?: number;
}

/**
 * Vertical navigation component with glassmorphism design
 */
export const VerticalNav = ({ open = true, onClose, width = 260 }: VerticalNavProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = isDark ? SIDEBAR_COLORS.dark : SIDEBAR_COLORS.light;
  const currentTenant = useContextStore((s) => s.currentTenant);
  const enabledModules = useContextStore((s) => s.enabledModules);
  const isGlobalAccess = useContextStore((s) => s.isGlobalAccess);

  const visibleSections = menuSections.filter((section) => {
    if (!section.moduleKey) return true;
    if (section.moduleKey === 'Automation') return enabledModules.automation;
    if (section.moduleKey === 'Governance') return enabledModules.governance;
    if (section.moduleKey === 'Integrations') return enabledModules.integrations;
    return true;
  });

  const handleNavigate = (path?: string) => {
    if (path) {
      navigate(path);
      if (onClose) {
        onClose();
      }
    }
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    if (path === ROUTES.HOME) {
      return location.pathname === ROUTES.HOME;
    }
    return location.pathname.startsWith(path.split('/:')[0]);
  };

  const collapsedWidth = 68;
  const currentWidth = open ? width : collapsedWidth;

  const drawerContent = (
    <Box
      sx={{
        width: currentWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: colors.bg,
        transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
      }}
    >
      {/* Logo / Brand */}
      <Box
        sx={{
          px: open ? 5 : 3,
          py: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          minHeight: 64,
        }}
      >
        <Logo iconOnly={!open} iconSize={32} wordmarkStyle />
      </Box>

      {/* Navigation Items — grouped by section */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', px: 2, py: 1 }}>
        {visibleSections.map((section: MenuSection, sIdx: number) => (
          <Box key={section.label ?? sIdx} sx={{ mb: 2 }}>
            {/* Section header */}
            {section.label && open && (
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  color: colors.textMuted,
                  px: 3,
                  pt: sIdx === 0 ? 0 : 2,
                  pb: 1,
                  ...(sIdx > 0 && {
                    borderTop: `1px solid ${colors.sectionBorder}`,
                    mt: 1,
                    pt: 2.5,
                  }),
                }}
              >
                {section.label}
              </Typography>
            )}

            <List disablePadding>
              {section.items
                .filter((item) => !item.requiresGlobalAccess || isGlobalAccess)
                .map((item) => {
                const active = isActive(item.path);
                const IconComponent = item.icon;
                const tenantLocked = item.requiresTenant && !currentTenant;
                const tooltipTitle = tenantLocked ? 'Select a tenant first' : item.title;

                const button = (
                  <ListItem key={item.title} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      selected={active && !tenantLocked}
                      disabled={tenantLocked}
                      onClick={() => !tenantLocked && handleNavigate(item.path)}
                      sx={{
                        borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
                        minHeight: 40,
                        px: open ? 3 : 2,
                        py: 1,
                        justifyContent: open ? 'initial' : 'center',
                        transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                        color: colors.text,
                        '&.Mui-disabled': {
                          opacity: 0.4,
                        },
                        '&.Mui-selected': {
                          background: 'var(--gradient-primary)',
                          color: colors.textActive,
                          boxShadow: 'var(--glow-primary)',
                          '& .MuiListItemIcon-root': {
                            color: colors.iconActive,
                          },
                          '& .MuiListItemText-primary': {
                            fontWeight: 600,
                            color: colors.textActive,
                          },
                          '&:hover': {
                            background: 'var(--gradient-primary)',
                            filter: 'brightness(1.1)',
                          },
                        },
                        '&:not(.Mui-selected):not(.Mui-disabled):hover': {
                          backgroundColor: colors.hoverBg,
                        },
                      }}
                    >
                      {IconComponent && (
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 2.5 : 0,
                            justifyContent: 'center',
                            color: active && !tenantLocked ? colors.iconActive : colors.icon,
                            '& svg': {
                              fontSize: '1.25rem',
                            },
                          }}
                        >
                          <IconComponent />
                        </ListItemIcon>
                      )}
                      {open && (
                        <ListItemText
                          primary={item.title}
                          primaryTypographyProps={{
                            fontSize: '0.8125rem',
                            fontWeight: active && !tenantLocked ? 600 : 400,
                            color: active && !tenantLocked ? colors.textActive : colors.text,
                            noWrap: true,
                          }}
                        />
                      )}
                    </ListItemButton>
                  </ListItem>
                );

                if (!open || tenantLocked) {
                  return (
                    <Tooltip key={item.title} title={tooltipTitle} placement="right" arrow>
                      {button}
                    </Tooltip>
                  );
                }

                return button;
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* Bottom section */}
      {open && (
        <Box
          sx={{
            px: 4,
            py: 3,
            borderTop: `1px solid ${colors.sectionBorder}`,
          }}
        >
          <Typography variant="caption" sx={{ color: colors.textMuted }}>
            CoOrdio Cortex v2.0
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: currentWidth,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: currentWidth,
          boxSizing: 'border-box',
          transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
          overflowX: 'hidden',
          borderRight: `1px solid ${colors.sectionBorder}`,
          backgroundColor: 'transparent',
          backgroundImage: 'none',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default VerticalNav;
